/**
 * Tracer streaks: the one effect that cannot be a round sprite.
 *
 * A tracer does not look like a dot, and not because of anything about the round. It looks like a
 * *line* because it is moving a kilometre a second and both the eye and the camera integrate over
 * a finite exposure — at 1/60 s a round at 1036 m/s smears itself across seventeen metres. Draw it
 * as a point and you get a dotted line of fireflies; draw the smear and you get a tracer.
 *
 * So each round becomes a quad stretched between where it was and where it is, widened
 * perpendicular to that segment *in view space* so it always faces the camera. Which means the
 * geometry is different every frame and there is no rest pose — hence `frustumCulled = false` and
 * a null bounding sphere, the same choice `pointSprites.ts` makes for the same reason.
 *
 * ## The streak is longer than the step
 *
 * The segment drawn is not the round's travel during one frame; it is its travel during
 * {@link TracerRibbons.exposureS}, extrapolated backwards along the velocity. Using the frame step
 * would make the tracers get *shorter* as the frame rate went up, which is exactly backwards from
 * what a camera does and would make the gun look different on every machine. Fixing the exposure
 * instead makes the streak a property of the round.
 *
 * `updateFrame(alpha)`: yes — the caller fills the buffer each frame and calls {@link commit}.
 * Allocation-free after construction.
 */

import { AdditiveBlending, BufferAttribute, BufferGeometry, Mesh } from 'three';
import { MeshBasicNodeMaterial } from 'three/webgpu';
import {
  attribute,
  cameraProjectionMatrix,
  cross,
  float,
  mix,
  modelViewMatrix,
  normalize,
  smoothstep,
  vec3,
  vec4,
} from 'three/tsl';

/**
 * Quad corners as (along, across): `along` 0 at the tail, 1 at the head; `across` ±1.
 * Order matches the index buffer below.
 */
const CORNERS = [
  [0, -1],
  [1, -1],
  [1, 1],
  [0, 1],
] as const;

export interface TracerOptions {
  capacity: number;
  name?: string;
}

export class TracerRibbons {
  readonly mesh: Mesh;
  readonly capacity: number;

  /** Shutter time the streak length is derived from [s]. A 1/60 s exposure, as a camera would. */
  exposureS = 1 / 60;

  private readonly geometry: BufferGeometry;
  private readonly material: MeshBasicNodeMaterial;
  private readonly heads: Float32Array;
  private readonly tails: Float32Array;
  private readonly colors: Float32Array;
  private readonly widths: Float32Array;
  private countValue = 0;

  constructor(options: TracerOptions) {
    this.capacity = options.capacity;
    const vertices = this.capacity * 4;

    this.heads = new Float32Array(vertices * 3);
    this.tails = new Float32Array(vertices * 3);
    this.colors = new Float32Array(vertices * 4);
    this.widths = new Float32Array(vertices);
    const corners = new Float32Array(vertices * 2);
    const indices = new Uint32Array(this.capacity * 6);

    for (let i = 0; i < this.capacity; i++) {
      for (let c = 0; c < 4; c++) {
        corners[(i * 4 + c) * 2] = CORNERS[c][0];
        corners[(i * 4 + c) * 2 + 1] = CORNERS[c][1];
      }
      const base = i * 4;
      indices.set([base, base + 1, base + 2, base, base + 2, base + 3], i * 6);
    }

    this.geometry = new BufferGeometry();
    // `position` is the head; three needs an attribute of that name to derive a draw range from.
    this.geometry.setAttribute('position', new BufferAttribute(this.heads, 3));
    this.geometry.setAttribute('tracerTail', new BufferAttribute(this.tails, 3));
    this.geometry.setAttribute('tracerColor', new BufferAttribute(this.colors, 4));
    this.geometry.setAttribute('tracerWidth', new BufferAttribute(this.widths, 1));
    this.geometry.setAttribute('corner', new BufferAttribute(corners, 2));
    this.geometry.setIndex(new BufferAttribute(indices, 1));
    this.geometry.boundingSphere = null;
    this.geometry.setDrawRange(0, 0);

    this.material = this.buildMaterial();
    this.mesh = new Mesh(this.geometry, this.material);
    this.mesh.name = options.name ?? 'tracers';
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.mesh.renderOrder = 11;
  }

  private buildMaterial(): MeshBasicNodeMaterial {
    const material = new MeshBasicNodeMaterial();
    material.transparent = true;
    material.depthWrite = false;
    // A tracer is burning phosphor; it adds light to whatever it crosses.
    material.blending = AdditiveBlending;

    const head = attribute<'vec3'>('position', 'vec3');
    const tail = attribute<'vec3'>('tracerTail', 'vec3');
    const corner = attribute<'vec2'>('corner', 'vec2');
    const width = attribute<'float'>('tracerWidth', 'float');
    const colour = attribute<'vec4'>('tracerColor', 'vec4');

    const viewHead = modelViewMatrix.mul(vec4(head, 1)).xyz;
    const viewTail = modelViewMatrix.mul(vec4(tail, 1)).xyz;
    const along = mix(viewTail, viewHead, corner.x);

    /*
     * Widen perpendicular to the streak *and* to the eye ray.
     *
     * `cross(segment, eyeRay)` is the screen-space normal of the segment, so the ribbon is edge-on
     * to nothing and always presents its full width. Using a fixed axis instead would make a
     * tracer fired straight at the camera collapse to a line — which is, admittedly, what it
     * really looks like, but it also makes every tracer flicker as the camera turns.
     */
    const segment = normalize(viewHead.sub(viewTail));
    const eyeRay = normalize(along);
    const side = normalize(cross(segment, eyeRay));
    const offset = side.mul(corner.y).mul(width);

    material.vertexNode = cameraProjectionMatrix.mul(vec4(along.add(offset), 1));

    /*
     * Bright at the head, fading to nothing at the tail, and soft across the width.
     *
     * The longitudinal fade is what makes it read as motion rather than as a stick: the front of
     * a real smear is where the round is now and the back is where it was, dimmer because it has
     * had less of the exposure.
     */
    const lengthwise = smoothstep(float(0), float(0.85), corner.x);
    const across = smoothstep(float(1), float(0.15), corner.y.abs());
    material.colorNode = vec3(colour.xyz);
    material.opacityNode = colour.w.mul(lengthwise).mul(across);
    return material;
  }

  get count(): number {
    return this.countValue;
  }

  /**
   * Place one streak.
   *
   * @param index    slot.
   * @param x,y,z    head position, render space [m].
   * @param vx,vy,vz velocity, render space [m/s] — the tail is extrapolated back along it.
   * @param widthM   half-width [m].
   */
  set(
    index: number,
    x: number,
    y: number,
    z: number,
    vx: number,
    vy: number,
    vz: number,
    r: number,
    g: number,
    b: number,
    a: number,
    widthM: number,
  ): void {
    if (index < 0 || index >= this.capacity) return;
    const tailX = x - vx * this.exposureS;
    const tailY = y - vy * this.exposureS;
    const tailZ = z - vz * this.exposureS;
    for (let c = 0; c < 4; c++) {
      const v = index * 4 + c;
      this.heads[v * 3] = x;
      this.heads[v * 3 + 1] = y;
      this.heads[v * 3 + 2] = z;
      this.tails[v * 3] = tailX;
      this.tails[v * 3 + 1] = tailY;
      this.tails[v * 3 + 2] = tailZ;
      this.colors[v * 4] = r;
      this.colors[v * 4 + 1] = g;
      this.colors[v * 4 + 2] = b;
      this.colors[v * 4 + 3] = a;
      this.widths[v] = widthM;
    }
  }

  /** Publish `count` streaks, uploading only the live prefix — see `particles.ts` on why. */
  commit(count: number): void {
    this.countValue = Math.min(count, this.capacity);
    const vertices = this.countValue * 4;
    for (const name of ['position', 'tracerTail', 'tracerColor', 'tracerWidth'] as const) {
      const attribute = this.geometry.getAttribute(name) as BufferAttribute;
      attribute.clearUpdateRanges();
      if (vertices === 0) {
        attribute.needsUpdate = false;
        continue;
      }
      attribute.addUpdateRange(0, vertices * attribute.itemSize);
      attribute.needsUpdate = true;
    }
    this.geometry.setDrawRange(0, this.countValue * 6);
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}
